import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, User, LogOut } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [settings, setSettings] = useState(null);
  const { user, logout, isAuthenticated } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    axios.get(`${API_URL}/settings`).then(r => setSettings(r.data)).catch(() => {});
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const NavLink = ({ to, children, className = '', ...rest }) => (
    <Link
      to={to}
      {...rest}
      className={`relative text-[11px] font-light tracking-[0.2em] uppercase text-black/80 
        hover:text-black transition-all duration-500 ease-out
        after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 
        after:h-[1px] after:bg-black/40 after:transition-all after:duration-500
        hover:after:w-full ${className}`}
    >
      {children}
    </Link>
  );

  return (
    <>
      <nav 
        data-testid="navbar" 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out
          ${isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-[0_1px_0_0_rgba(0,0,0,0.05)]' 
            : 'bg-white'
          }`}
      >
        {/* Top accent line */}
        <div className="h-[1px] bg-gradient-to-r from-transparent via-black/10 to-transparent" />
        
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between px-6 md:px-12 lg:px-16 py-5 md:py-6">
            
            {/* Left Navigation - Desktop */}
            <div className="hidden lg:flex items-center gap-10 flex-1">
              <NavLink to="/catalogo" data-testid="catalog-link">
                Catálogo
              </NavLink>
              <NavLink to="/catalogo-3d" data-testid="catalog-3d-link">
                Catálogo 3D
              </NavLink>
              <NavLink to="/promocoes" data-testid="promotions-link">
                Promoções
              </NavLink>
            </div>

            {/* Center - Logo */}
            <Link 
              to="/" 
              data-testid="logo-link" 
              className="flex items-center justify-center flex-shrink-0 group"
            >
              <img 
                src={settings?.logo_dark_image || "https://customer-assets.emergentagent.com/job_loja-digital-56/artifacts/tun8autg_PEQUENA_PRETA.png"} 
                alt="casa in gold by Fhiama Fonseca" 
                className="h-20 md:h-28 lg:h-32 w-auto transition-transform duration-700 ease-out group-hover:scale-[1.02]"
              />
            </Link>

            {/* Right Navigation - Desktop */}
            <div className="hidden lg:flex items-center justify-end gap-10 flex-1">
              <NavLink to="/sobre" data-testid="about-link">
                Sobre
              </NavLink>
              <NavLink to="/contato" data-testid="contact-link">
                Contato
              </NavLink>
              
              {isAuthenticated ? (
                <div className="flex items-center gap-6 ml-4 pl-6 border-l border-black/10">
                  {user?.is_admin && (
                    <NavLink to="/admin" data-testid="admin-link">
                      Admin
                    </NavLink>
                  )}
                  {!user?.is_admin && (
                    <NavLink to="/meus-pedidos" data-testid="my-orders-link">
                      Meus Pedidos
                    </NavLink>
                  )}
                  <Link 
                    to="/perfil" 
                    data-testid="profile-link" 
                    className="text-black/70 hover:text-black transition-colors duration-300"
                    aria-label="Perfil"
                  >
                    <User className="w-[18px] h-[18px]" strokeWidth={1.25} />
                  </Link>
                  <button
                    onClick={handleLogout}
                    data-testid="logout-button"
                    className="text-black/70 hover:text-black transition-colors duration-300"
                    aria-label="Sair"
                  >
                    <LogOut className="w-[18px] h-[18px]" strokeWidth={1.25} />
                  </button>
                </div>
              ) : (
                <NavLink to="/login" data-testid="login-link" className="ml-4 pl-6 border-l border-black/10">
                  Entrar
                </NavLink>
              )}
              
              {/* Cart */}
              <Link 
                to="/carrinho" 
                data-testid="cart-link" 
                className="relative text-black/70 hover:text-black transition-colors duration-300 ml-2"
                aria-label="Carrinho"
              >
                <ShoppingBag className="w-[18px] h-[18px]" strokeWidth={1.25} />
                {cartCount > 0 && (
                  <span
                    data-testid="cart-count"
                    className="absolute -top-1.5 -right-1.5 bg-black text-white text-[9px] 
                      w-4 h-4 rounded-full flex items-center justify-center font-medium
                      animate-in fade-in zoom-in duration-200"
                  >
                    {cartCount}
                  </span>
                )}
              </Link>
            </div>

            {/* Mobile Actions */}
            <div className="flex lg:hidden items-center gap-5">
              <Link 
                to="/carrinho" 
                data-testid="cart-link-mobile" 
                className="relative text-black/70 hover:text-black transition-colors duration-300"
                aria-label="Carrinho"
              >
                <ShoppingBag className="w-5 h-5" strokeWidth={1.25} />
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-black text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-medium">
                    {cartCount}
                  </span>
                )}
              </Link>
              
              <button
                onClick={() => setIsOpen(!isOpen)}
                data-testid="mobile-menu-button"
                className="text-black/70 hover:text-black transition-colors duration-300 p-1"
                aria-label={isOpen ? 'Fechar menu' : 'Abrir menu'}
              >
                <div className="relative w-5 h-5">
                  <span className={`absolute left-0 w-5 h-[1.5px] bg-current transition-all duration-300 ease-out
                    ${isOpen ? 'top-[9px] rotate-45' : 'top-[4px]'}`} />
                  <span className={`absolute left-0 top-[9px] w-5 h-[1.5px] bg-current transition-all duration-300 ease-out
                    ${isOpen ? 'opacity-0 scale-x-0' : 'opacity-100'}`} />
                  <span className={`absolute left-0 w-5 h-[1.5px] bg-current transition-all duration-300 ease-out
                    ${isOpen ? 'top-[9px] -rotate-45' : 'top-[14px]'}`} />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Bottom accent line */}
        <div className={`h-[1px] transition-all duration-500 ${isScrolled ? 'bg-transparent' : 'bg-black/5'}`} />
      </nav>

      {/* Spacer to prevent content jump */}
      <div className="h-[89px] md:h-[113px] lg:h-[129px]" />

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-500
          ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setIsOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <div 
        data-testid="mobile-menu"
        className={`fixed top-[89px] md:top-[113px] left-0 right-0 bg-white z-40 lg:hidden 
          transform transition-all duration-500 ease-out shadow-lg
          ${isOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'}`}
      >
        <div className="px-6 py-8 space-y-1">
          {[
            { to: '/catalogo', label: 'Catálogo' },
            { to: '/catalogo-3d', label: 'Catálogo 3D' },
            { to: '/promocoes', label: 'Promoções' },
            { to: '/sobre', label: 'Sobre' },
            { to: '/contato', label: 'Contato' },
          ].map((item, index) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.to === '/catalogo-3d' ? 'catalog-3d-link-mobile' : `mobile-link-${item.to.replace('/', '')}`}
              className="block py-3 text-[13px] font-light tracking-[0.15em] uppercase text-black/70 
                hover:text-black hover:pl-2 transition-all duration-300 border-b border-black/5"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {item.label}
            </Link>
          ))}
          
          <div className="pt-4 mt-4 border-t border-black/10">
            {isAuthenticated ? (
              <div className="space-y-1">
                {user?.is_admin && (
                  <Link
                    to="/admin"
                    className="block py-3 text-[13px] font-light tracking-[0.15em] uppercase text-black/70 
                      hover:text-black hover:pl-2 transition-all duration-300"
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to="/perfil"
                  className="block py-3 text-[13px] font-light tracking-[0.15em] uppercase text-black/70 
                    hover:text-black hover:pl-2 transition-all duration-300"
                >
                  Minha Conta
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left py-3 text-[13px] font-light tracking-[0.15em] uppercase 
                    text-black/50 hover:text-black hover:pl-2 transition-all duration-300"
                >
                  Sair
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="block py-3 text-[13px] font-light tracking-[0.15em] uppercase text-black/70 
                  hover:text-black hover:pl-2 transition-all duration-300"
              >
                Entrar
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;